import { bbox } from "@turf/turf";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LngLatBoundsLike, Map as MapComponent } from "react-map-gl/maplibre";
import { mapTransformRequest } from "../../../../../../API/AureaApi";
import { mapStyle } from "../../../../../../Component/Map";
import BaseLayer from "../../../../../../Component/Map/Layers/BaseLayer";
import TaskmapWizard from "../TaskmapWizard";
import VigourLayer from "../../../../../../Component/Map/Layers/VigourLayer";
import { useTranslation } from "react-i18next";
import { TaskmapProduct } from "../../../../../../API/taskmap";
import { TaskmapProps } from "../../Taskmap";
import VigourVariableRateStep from "./VigourVariableRateStep";
import { DownloadTaskmapStep } from "../DownloadTaskmapStep";
import { useParams } from "react-router";
import { getTaskmap } from "../../../../../../API/taskmap";
import { getFileNameFromHeader } from "../../utils/file-utils";
import TaskmapPopup from "../../TaskmapPopup";
import { getCutoffs, getSortedData } from "../../utils/data-utils";
import {
  belongsToFamily,
  downloadTaskmap,
  getTaskmapHistory,
  TaskmapHistoryEntry,
} from "../../../../../../API/taskmapConfigHistory";
import TaskmapConfigHistoryModal from "../../TaskmapConfigHistoryModal";
import TaskmapStepHeader from "../TaskmapStepHeader";
import { VigourFeatureCollection } from "../../../../../../Component/Map/Layers/layer.types";
import { CutoffRange } from "../../taskmap.types";

export const VIGOUR_CLASSES = [1, 2, 3, 4, 5, 6, 7];
export type VariableRates = Map<number, number>;
const getInitialRates = () => {
  return new Map(VIGOUR_CLASSES.map((vigourClass) => [vigourClass, 0]));
};

export default function VigourTaskmap({
  geojson,
  productKey,
}: TaskmapProps): React.ReactNode {
  const { t } = useTranslation();
  const [cutoff, setCutoff] = useState<CutoffRange>({ min: 1, max: 1 });
  const [rates, setRates] = useState<VariableRates>(getInitialRates);

  const [machine, setMachine] = useState<string>("");
  const [terminal, setTerminal] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const totalTreeCount = geojson.features.length;

  const { resultSetId, fieldId } = useParams();
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [taskmap, setTaskmap] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<TaskmapHistoryEntry[]>([]);

  useEffect(() => {
    if (!fieldId) return;
    getTaskmapHistory(fieldId)
      .then((entries) =>
        setHistory(
          entries.filter((entry) =>
            belongsToFamily(entry.product_sku, "vigour"),
          ),
        ),
      )
      .catch(() => setHistory([]));
  }, [fieldId]);

  const onSubmit = useCallback(async () => {
    let parameters;

    setLoading(true);

    if (isNonVariableRootPrunner) {
      parameters = {
        cutoff_low: `${cutoff.min}`,
        cutoff_high: `${cutoff.max}`,
        rate_low: 0,
        rate_high: 100,
        machine_parameters: {
          type: machine,
        },
      };
    } else {
      if (!rates) {
        return;
      }
      const ratesParameter = [...rates.entries()].map(([category, rate]) => {
        return { lower: category, upper: category, rate };
      });

      parameters = {
        zones: ratesParameter,
        machine_parameters: {
          type: machine,
        },
      };
    }
    const response = await getTaskmap(
      {
        terminal: {
          type: terminal,
        },
        parameters,
      },
      resultSetId,
    );
    setLoading(false);

    if (response !== null && !response.ok) {
      throw new Error("Request failed with status " + response.status);
    }
    if (response !== null) {
      setPopupOpen(true);
      setTaskmap(
        new File([await response.blob()], getFileNameFromHeader(response)),
      );
    }
  }, [machine, terminal, rates, cutoff]);

  const onDownloadAgain = useCallback(async (entry: TaskmapHistoryEntry) => {
    setHistoryOpen(false);
    setLoading(true);
    setError(null);
    const response = await downloadTaskmap(entry.id);

    setLoading(false);

    if (!response || !response.ok) {
      setError(t("text.error_occured"));
      return;
    }
    setPopupOpen(true);
    setTaskmap(
      new File([await response.blob()], getFileNameFromHeader(response)),
    );
  }, []);

  const sortedData = useMemo(
    () => getSortedData(geojson, productKey),
    [geojson, productKey],
  );

  useEffect(() => setCutoff(getCutoffs(sortedData)), [sortedData]);

  useEffect(() => {
    setRates(getInitialRates());
    setCutoff(getCutoffs(sortedData));
  }, [machine, terminal]);

  const isNonVariableRootPrunner = false;

  return (
    <>
      <TaskmapWizard
        product={TaskmapProduct.VIGOUR}
        setMachine={setMachine}
        setTerminal={setTerminal}
      >
        {/* kwh does not support variable root pruning */}
        {isNonVariableRootPrunner ? (
          <DownloadTaskmapStep
            cutoff={cutoff}
            setCutoff={setCutoff}
            header={t("heading.Vigour Class")}
            label={t("heading.Vigour Class")}
            sortedData={sortedData}
            totalTreeCount={totalTreeCount}
            onSubmit={onSubmit}
            loading={loading}
          />
        ) : (
          <VigourVariableRateStep
            setRates={setRates}
            rates={rates}
            sortedData={sortedData}
            onSubmit={onSubmit}
            loading={loading}
            header={
              <TaskmapStepHeader
                label={t("heading.Vigour Class")}
                showHistory={history.length > 0}
                onHistoryClick={() => setHistoryOpen(true)}
                marginLeft="10px"
              />
            }
          />
        )}
      </TaskmapWizard>
      {error && (
        <div style={{ color: "red", padding: "8px 16px" }}>{error}</div>
      )}
      <MapComponent
        mapStyle={mapStyle}
        transformRequest={mapTransformRequest}
        initialViewState={{
          bounds: bbox(geojson) as LngLatBoundsLike,
        }}
        style={{ width: "100%", height: "70vh" }}
      >
        <BaseLayer />
        {isNonVariableRootPrunner ? (
          <VigourLayer
            geojson={geojson as VigourFeatureCollection}
            cutoff={cutoff}
          />
        ) : (
          <VigourLayer
            geojson={geojson as VigourFeatureCollection}
            rates={rates}
          />
        )}
      </MapComponent>
      <TaskmapPopup
        taskmap={taskmap}
        open={popupOpen}
        setOpen={(open) => {
          setPopupOpen(open);
          if (!open) {
            setTaskmap(null);
          }
        }}
      ></TaskmapPopup>
      <TaskmapConfigHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        product="vigour"
        history={history}
        onDownloadAgain={onDownloadAgain}
      />
    </>
  );
}
